import { useState, useEffect } from 'react';
import MetricCard from '../shared/MetricCard';
import ResponseChart from '../shared/charts/ResponseChart';
import { FileText, Users, CheckCircle, Clock, TrendingUp, Zap, Plus, Inbox, BarChart3, TrendingDown, Crown, X, Sparkles } from 'lucide-react';
import useAuth from '../../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { getDashboardStats } from '../../../services/responseService';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../common/LoadingSpinner';
import Modal from '../../common/Modal';

const FREE_FORM_LIMIT = 5;

const Overview = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [currentPlan, setCurrentPlan] = useState('free');

  // Fetch dashboard data and user plan
  useEffect(() => {
    fetchDashboardData();
    fetchUserPlan();
  }, []);

  const fetchUserPlan = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API_URL}/users/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setCurrentPlan(userData.data?.subscription_plan || 'free');
      }
    } catch (error) {
      console.error('Error fetching user plan:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await getDashboardStats();
      if (response.success) {
        setDashboardData(response.data);
      } else {
        throw new Error('Failed to fetch dashboard data');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard stats');
      // Set empty data on error
      setDashboardData({
        totalForms: 0,
        totalResponses: 0,
        avgCompletionRate: 0,
        avgSessionDuration: 0,
        activeForms: 0,
        todayResponses: 0,
        avgRating: 0,
        responsesByDate: {},
        topForms: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateForm = () => {
    // Check form limit for free users
    if (currentPlan === 'free' && dashboardData?.totalForms >= FREE_FORM_LIMIT) {
      setShowUpgradeModal(true);
      return;
    }
    navigate('/form-builder');
  };

  // Transform real data for display
  const hasData = dashboardData?.totalForms > 0;

  // Transform responsesByDate to chart format
  const responseData = dashboardData?.responsesByDate
    ? Object.entries(dashboardData.responsesByDate).map(([date, responses]) => ({
        date: new Date(date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        }),
        responses
      }))
    : [];

  // Funnel data - placeholder for now
  const funnelData = hasData ? [
    { stage: 'Started', value: dashboardData.totalResponses },
    { stage: 'Question 1', value: Math.floor(dashboardData.totalResponses * 0.9) },
    { stage: 'Question 2', value: Math.floor(dashboardData.totalResponses * 0.8) },
    { stage: 'Question 3', value: Math.floor(dashboardData.totalResponses * 0.7) },
    { stage: 'Completed', value: Math.floor(dashboardData.totalResponses * 0.6) },
  ] : [];

  // Use real top forms from API
  const topForms = dashboardData?.topForms || [];

  // Empty State Component
  const EmptyState = ({ icon: Icon, title, description, actionText, onAction }) => (
    <div className="flex flex-col items-center justify-center py-8 sm:py-12 px-4">
      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
        <Icon className="text-gray-400" size={24} />
      </div>
      <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 text-center">{title}</h4>
      <p className="text-gray-500 text-xs sm:text-sm text-center mb-4 sm:mb-6 max-w-sm px-2">{description}</p>
      {actionText && onAction && (
        <button
          onClick={onAction}
          className="px-4 py-2 sm:px-5 sm:py-2.5 bg-blue text-white font-medium rounded-lg hover:bg-blue/90 transition-colors duration-200 flex items-center gap-2 text-sm sm:text-base"
        >
          <Plus size={16} className="sm:w-[18px] sm:h-[18px]" />
          <span>{actionText}</span>
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6 lg:space-y-8 p-3 sm:p-4 lg:p-6">
      {/* Welcome Header */}
      <div className="text-center lg:text-left">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-1 sm:mb-2 break-words px-2 sm:px-0">
          Welcome back{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}!
        </h1>
        <p className="text-gray-600 text-sm sm:text-base lg:text-lg px-2 sm:px-0">
          {hasData
            ? "Here's how your forms are performing today"
            : "Ready to create your first form?"}
        </p>
      </div>
      <div className="px-2 sm:px-0">
        <button
          onClick={handleCreateForm}
          className="px-4 py-2 sm:px-6 sm:py-3 bg-blue-600 text-white font-semibold rounded-lg sm:rounded-xl hover:bg-blue/90 transition-colors duration-200 flex items-center gap-2 justify-center lg:justify-start whitespace-nowrap w-full sm:w-auto text-sm sm:text-base"
        >
          <Plus size={18} className="sm:w-5 sm:h-5" />
          <span>Create New Form</span>
        </button>
      </div>

      {/* Metrics Cards - Improved Layout */}
      {loading ? (
        <div className="text-center py-12">
          <LoadingSpinner size="lg" variant="dots" />
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
          <MetricCard
            title="Total Forms Created"
            value={dashboardData?.totalForms?.toString() || "0"}
            icon={FileText}
            trend={hasData ? "up" : undefined}
            trendValue={hasData ? "+3" : undefined}
            color="blue"
          />
          <MetricCard
            title="Total Responses"
            value={dashboardData?.totalResponses?.toLocaleString() || "0"}
            icon={Users}
            trend={hasData ? "up" : undefined}
            trendValue={hasData ? "+12%" : undefined}
            color="mint"
          />
          <MetricCard
            title="Avg Completion Rate"
            value={dashboardData?.avgCompletionRate?.toString() || "0"}
            suffix="%"
            icon={CheckCircle}
            trend={hasData ? "up" : undefined}
            trendValue={hasData ? "+5%" : undefined}
            color="greenBtn"
          />
          <MetricCard
            title="Avg Session Duration"
            value={dashboardData?.avgSessionDuration?.toFixed(1) || "0"}
            suffix="min"
            icon={Clock}
            color="blue"
          />
        </div>
      )}

      {/* Response Chart - Full Width with Better Structure */}
      <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 lg:mb-8 gap-3 sm:gap-4">
          <div>
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-1">Response Trends</h3>
            <p className="text-gray-500 text-xs sm:text-sm">Daily response activity over the last two weeks</p>
          </div>
          {hasData && dashboardData.weeklyTrend !== undefined && (
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              dashboardData.weeklyTrend >= 0 ? 'bg-blue/5' : 'bg-red-50'
            }`}>
              {dashboardData.weeklyTrend >= 0 ? (
                <TrendingUp size={18} className="text-blue" />
              ) : (
                <TrendingDown size={18} className="text-red-600" />
              )}
              <span className={`text-base font-semibold ${
                dashboardData.weeklyTrend >= 0 ? 'text-blue' : 'text-red-600'
              }`}>
                {dashboardData.weeklyTrend >= 0 ? '+' : ''}{dashboardData.weeklyTrend}% this week
              </span>
            </div>
          )}
        </div>
        {responseData.length > 0 ? (
          <div className="h-64 sm:h-80 md:h-96 lg:h-[350px] w-full">
            <ResponseChart data={responseData} />
          </div>
        ) : (
          <EmptyState
            icon={BarChart3}
            title="No Response Data Yet"
            description="Start collecting responses to see trends and analytics."
            actionText="Create Your First Form"
            onAction={handleCreateForm}
          />
        )}
      </div>

     {/* Top Performing Forms - Enhanced Design */}
<div className="bg-white rounded-xl sm:rounded-2xl border border-gray-100 overflow-hidden flex flex-col h-full">
  <div className="p-4 sm:p-6 lg:p-8 border-b border-gray-100 flex-shrink-0">
    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-1">Top Performing Forms</h3>
    <p className="text-gray-500 text-xs sm:text-sm">Your most engaging forms this month</p>
  </div>
  
  {topForms.length > 0 ? (
    <div className="flex-1 overflow-hidden">
      <div className="h-full overflow-y-auto divide-y divide-gray-100">
        {topForms.map((form, index) => (
          <div
            key={index}
            className="p-4 sm:p-6 transition-all duration-300 hover:bg-gray-50 cursor-pointer group"
          >
            <div className="flex items-start justify-between mb-3 sm:mb-4 gap-2">
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-gray-900 text-base sm:text-lg mb-1 group-hover:text-blue-600 transition-colors truncate">
                  {form.name}
                </h4>
                <div className="flex items-center gap-2 text-gray-400 text-xs">
                  <Clock size={12} className="flex-shrink-0" />
                  <span className="truncate">Updated {form.lastUpdated}</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 bg-mint/10 rounded-lg flex-shrink-0">
                <Zap size={14} className="text-mint" />
                <span className="text-mint text-xs sm:text-sm font-bold">{form.engagement}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-6">
              <div className="min-w-0">
                <div className="text-gray-500 text-[10px] sm:text-xs uppercase tracking-wide mb-1 sm:mb-1.5 truncate">Responses</div>
                <div className="font-bold text-gray-900 text-lg sm:text-xl truncate">{form.responses.toLocaleString()}</div>
              </div>
              <div className="min-w-0">
                <div className="text-gray-500 text-[10px] sm:text-xs uppercase tracking-wide mb-1 sm:mb-1.5 truncate">Completion</div>
                <div className="font-bold text-greenBtn text-lg sm:text-xl truncate">{form.completion}%</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  ) : (
    <div className="flex-1 flex items-center justify-center">
      <EmptyState
        icon={Inbox}
        title="No Forms Yet"
        description="Create your first form to start collecting responses and see performance metrics here."
        actionText="Create Form"
        onAction={handleCreateForm}
      />
    </div>
  )}
</div>

      {/* Quick Stats Bar - Enhanced Design */}
      {!loading && (
        <div className="bg-blue-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            <div className="text-center md:text-left">
              <div className="text-white/70 text-xs sm:text-sm font-medium mb-1 sm:mb-2 uppercase tracking-wide truncate">Active Forms</div>
              <div className="text-white text-2xl sm:text-3xl lg:text-4xl font-bold">
                {dashboardData?.activeForms || '0'}
              </div>
            </div>
            <div className="text-center md:text-left border-l-0 md:border-l border-white/20 md:pl-8">
              <div className="text-white/70 text-xs sm:text-sm font-medium mb-1 sm:mb-2 uppercase tracking-wide truncate">Today's Responses</div>
              <div className="text-white text-2xl sm:text-3xl lg:text-4xl font-bold">
                {dashboardData?.todayResponses || '0'}
              </div>
            </div>
            <div className="text-center md:text-left border-l-0 md:border-l border-white/20 md:pl-8">
              <div className="text-white/70 text-xs sm:text-sm font-medium mb-1 sm:mb-2 uppercase tracking-wide truncate">Avg. Rating</div>
              <div className="text-white text-2xl sm:text-3xl lg:text-4xl font-bold break-all">
                {dashboardData?.avgRating > 0 ? dashboardData.avgRating.toFixed(1) : '--'}
                <span className="text-lg sm:text-xl lg:text-2xl text-white/70">
                  {dashboardData?.avgRating > 0 ? '/10' : ''}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      <Modal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        showCloseButton={false}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Crown size={20} className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Upgrade Your Plan</h3>
            </div>
            <button
              onClick={() => setShowUpgradeModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
          <div className="space-y-4">
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles size={32} className="text-blue-600" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                You've reached your form limit!
              </h4>
              <p className="text-gray-600 text-sm">
                Free accounts can create up to {FREE_FORM_LIMIT} forms. Upgrade to Creator to unlock unlimited forms and more amazing features!
              </p>
            </div>

            <div className="bg-blue-50 rounded-xl p-4 space-y-2">
              <p className="text-sm font-semibold text-blue-900">Creator Plan includes:</p>
              <ul className="text-sm text-blue-800 space-y-1">
                <li className="flex items-center gap-2">
                  <CheckCircle size={14} className="text-blue-600" />
                  Unlimited forms
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={14} className="text-blue-600" />
                  Unlimited responses
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={14} className="text-blue-600" />
                  Advanced analytics
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={14} className="text-blue-600" />
                  Priority support
                </li>
              </ul>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-semibold"
              >
                Maybe Later
              </button>
              <button
                onClick={() => {
                  setShowUpgradeModal(false);
                  navigate('/dashboard/account?section=plan');
                }}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-semibold flex items-center justify-center gap-2"
              >
                <Zap size={18} />
                Upgrade Now
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Overview;